'use client'

import Link from 'next/link'
import s from './main-nav.module.css'
import cx from 'classnames'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useHover } from 'usehooks-ts'
import { UiPlacement, UiPlacementType } from '@/types/api/ui-placement'
import { MenuItemsType } from '../../../../v2/organisms/navigation/navigation-index/navigation-index'
import { uppercaseEachWord } from '@/utils/uppercaseEachWord'
import { Supplier } from '@/types/Supplier'
import { navItemOnClickGTMEvent } from '@/utils/gtmEvents/navItemOnClickGTMEvent'
import { mapSupplierToCardProps } from '@/utils/mapSupplierToCardProps'
import { mapArticleToCardProps } from '@/utils/mapArticleToCardProps'
import { Article } from '@/types/Article'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { ArticleCategory } from '@/types/api/article-category'
import Card, { CardItem, CardType } from '@/components/v3/atoms/card/card'
import { NavSubcategoryList } from '@/components/v3/organisms/navigation/nav-subcategory-list/nav-subcategory-list'
import { NewBadge } from '@/components/v3/atoms/new-badge/new-badge'
import { CSS_BREAKPOINTS_V2, ROUTES } from '@/constants'
import { SliderWithNavigation } from '@/components/v3/molecules/custom-sliders/slider-with-navigation/slider-with-navigation'
import { NavShopMegaMenu } from '@/components/v3/organisms/navigation/nav-shop-mega-menu/nav-shop-mega-menu'
import { hasShopMegaMenuLinks } from '@/components/v3/organisms/navigation/nav-shop-mega-menu/shop-mega-menu-links'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import type { ShopMegaMenuData } from '@/types/api/shop-mega-menu'

function splitBusinessCategories(
  arr: MenuItemsType[0]['subcategories']
): UiPlacement[][] {
  const subcategories = []
  const columnKey = arr[0].metadata?.columnNumber ? 'columnNumber' : 'rowTitle'
  const uniqueColumnNumbers = [
    ...new Set(arr.map((item) => item?.metadata?.[columnKey])),
  ]

  for (let i = 0; i < uniqueColumnNumbers.length; i++) {
    const column = arr.filter(
      (item) => item?.metadata?.[columnKey] === uniqueColumnNumbers[i]
    )
    subcategories.push(column)
  }

  return subcategories
}

export function splitSubcategories(
  arr: MenuItemsType[0]['subcategories']
): UiPlacement[][] {
  const subcategories = []

  if (arr[0]?.metadata?.rowTitle || arr[0]?.metadata?.columnNumber) {
    return splitBusinessCategories(arr)
  }

  // Article subcategories fill columns of four, but never more than two
  // columns: once a category grows past eight links the columns get taller
  // instead of a third column appearing beside the featured slider.
  const maxColumns = 2
  const linksPerColumn = Math.max(4, Math.ceil(arr.length / maxColumns))

  for (let i = 0; i < arr.length; i += linksPerColumn) {
    subcategories.push(arr.slice(i, i + linksPerColumn))
  }

  return subcategories
}

export function getMainCategoryObject(
  item: MenuItemsType[0]['mainItem']
): UiPlacement['metadata'] | ArticleCategory {
  return item.type === UiPlacementType.METADATA
    ? item.metadata
    : item.article_categories
}

export function getSubcategoryObject(
  parentItem: UiPlacement['metadata'],
  item: MenuItemsType[0]['subcategories'][0]
): UiPlacement['metadata'] {
  const overrides = item?.metadata?.overrides
  const articleSubcategoryObj = overrides || item?.article_subcategories
  let slug = `/${parentItem?.slug}/${articleSubcategoryObj?.slug}`
  if (overrides?.slug) {
    slug = overrides.slug
  }

  const overridesLinkName = overrides?.name

  if (item.type === UiPlacementType.TAG) {
    return {
      slug: `/wedding-venues?tag=${item.tag?.id}`,
      name: overridesLinkName || uppercaseEachWord(item.tag?.name || ''),
      rowTitle: item.metadata?.rowTitle,
    }
  }

  return item?.type === UiPlacementType.BUSINESS_CATEGORY
    ? {
        slug: `/wedding-suppliers/${item.business_category?.slug}`,
        name:
          overridesLinkName ||
          uppercaseEachWord(item.business_category?.mapping_name || ''),
        rowTitle: item.metadata?.rowTitle,
      }
    : { ...articleSubcategoryObj, slug }
}

function getSliderItems(
  mainCatObj: UiPlacement['metadata'],
  featuredArticles?: Article[],
  navbarSuppliers?: Supplier[],
  navbarVenues?: Supplier[]
) {
  if (mainCatObj?.showSuppliers) {
    return mapSupplierToCardProps(navbarSuppliers)
  }
  if (mainCatObj?.showVenues) {
    return mapSupplierToCardProps(navbarVenues)
  }
  return mapArticleToCardProps(
    featuredArticles
      ?.filter((article) => article.category === mainCatObj?.slug)
      .map((article) => ({
        id: article.id,
        title: article.title,
        path: article.path,
        published_date: new Date(article.published_date || ''),
        article_subcategories: article.article_subcategories!,
        article_categories: article.article_categories!,
        hero_image_ref: article.hero_image_ref!,
      }))
  )
}

function NavDropdown({
  mainCatObj,
  subcategories,
  showSlider,
  sliderItems,
  cardType,
  handleCloseMenu,
}: {
  mainCatObj: ReturnType<typeof getMainCategoryObject>
  subcategories: UiPlacement[][]
  showSlider: boolean
  sliderItems?: CardItem[]
  cardType: CardType
  handleCloseMenu: () => void
}) {
  const subcategoryCount = subcategories.length
  return (
    <div className={s.dropdownWrapper}>
      <div className={s.dropdownNavWrapper}>
        <Link
          href={`/${mainCatObj?.slug}`}
          className={s.subcategoryTitleWrapper}
          onClick={() => {
            handleCloseMenu()
            navItemOnClickGTMEvent(mainCatObj?.name)
          }}
        >
          <span className={s.subcategoryTitle}>{mainCatObj?.name}</span>
          <span className={s.subcategoryTitleText}>View all</span>
        </Link>
        {subcategories.map((row, idx) => (
          <NavSubcategoryList
            key={idx}
            row={row}
            mainCatObj={mainCatObj}
            handleCloseMenu={handleCloseMenu}
            variant="desktop"
          />
        ))}
      </div>
      {showSlider && !!sliderItems?.length && (
        <div className={s.sliderContainer}>
          <SliderWithNavigation
            title={<span className={s.title}>Featured </span>}
            keenSliderOptions={{
              breakpoints: {
                [`(min-width: ${CSS_BREAKPOINTS_V2.desktopL})`]: {
                  slides: {
                    perView: subcategoryCount < 3 ? 'auto' : 2,
                    spacing: 24,
                  },
                },
              },
            }}
            navigationVisible={sliderItems.length > 3}
            navigationIconsColor="#fff"
          >
            {sliderItems?.map((item) => {
              return (
                <div
                  key={`${item.id}-slider`}
                  onClick={() => handleCloseMenu()}
                >
                  <Card
                    primaryColor={'#fff'}
                    reverseVertical
                    size="Small"
                    adjustToSlider
                    navMegaMenu
                    itemId={item.id!}
                    header={item.title}
                    path={item.path}
                    date={item.date}
                    category={item?.category || ''}
                    locations={item.locations}
                    type={cardType === 'Article' ? 'Article' : 'Supplier'}
                    imageUrl={item.imageUrl!}
                    imageAlt={item.imageAlt!}
                  />
                </div>
              )
            })}
          </SliderWithNavigation>
        </div>
      )}
    </div>
  )
}

type Props = {
  featuredArticles?: Article[]
  navbarSuppliers?: Supplier[]
  navbarVenues?: Supplier[]
  menuItems: MenuItemsType
  shopMegaMenu?: ShopMegaMenuData | null
}

const SHOP_PANEL_ID = 'shop-mega-menu-panel'

export const MainNav = ({
  featuredArticles,
  navbarSuppliers,
  navbarVenues,
  menuItems,
  shopMegaMenu,
}: Props) => {
  const [hoveredCategory, setHoveredCategory] = useState('')
  // Which gesture opened the Shop panel: a hover closes when the pointer leaves
  // the nav, a keyboard open stays put until ESC or a click.
  const [shopOpenedBy, setShopOpenedBy] = useState<'hover' | 'keyboard' | null>(
    null
  )
  const containerRef = useRef<HTMLElement>(null!)
  const shopTriggerRef = useRef<HTMLButtonElement>(null)
  const shopPanelRef = useRef<HTMLDivElement>(null)
  const itemHovered = useHover(containerRef)
  const breakpoint = useBreakpoint()
  const isDesktopSmall = breakpoint === 'desktop-s'
  const { isEnabled } = useFeatureFlag()

  const hasShopMenu = hasShopMegaMenuLinks(shopMegaMenu)
  const isShopOpen = hasShopMenu && shopOpenedBy !== null

  const handleCloseShop = () => setShopOpenedBy(null)

  useEffect(() => {
    if (!itemHovered) {
      setHoveredCategory('')
      setShopOpenedBy((current) => (current === 'hover' ? null : current))
    }
  }, [itemHovered])

  // ESC closes and hands focus back to the trigger, so a keyboard user is not
  // dropped at the top of the document.
  useEffect(() => {
    if (!isShopOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setShopOpenedBy(null)
      shopTriggerRef.current?.focus()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isShopOpen])

  useFocusTrap(shopPanelRef, isShopOpen)

  const handleCloseMenu = () => setHoveredCategory('')

  // Shop is a static entry (not part of the data-driven category menu). The
  // design places it right after Venues, so we splice it in after that item —
  // falling back to the end of the list if Venues is ever absent.
  const venuesIndex = menuItems.findIndex(
    (item) => item.mainItem.metadata?.name === 'Venues'
  )
  // Affiliate Shop is hidden until the `affiliateShop` feature flag is enabled.
  const shopNavItem = isEnabled('affiliateShop') ? (
    <li
      className={s.navItem}
      key="shop"
      onMouseEnter={() => {
        setHoveredCategory('')
        if (hasShopMenu) setShopOpenedBy('hover')
      }}
    >
      {hasShopMenu ? (
        // A trigger rather than a link once there is a panel to open: it is what
        // carries `aria-expanded`, and Enter/Space toggle it for free. The link
        // to /shop lives on the panel's heading, as it does for a category.
        <button
          ref={shopTriggerRef}
          type="button"
          className={cx(
            s.navLink,
            s.navLinkSuppliers,
            s.navLinkShop,
            s.navShopTrigger
          )}
          aria-expanded={isShopOpen}
          aria-controls={SHOP_PANEL_ID}
          onFocus={() => setShopOpenedBy('keyboard')}
          onClick={() => {
            navItemOnClickGTMEvent('Shop')
            setShopOpenedBy(isShopOpen ? null : 'keyboard')
          }}
        >
          Shop
          <NewBadge className={s.navNewBadge} />
        </button>
      ) : (
        <Link
          href={ROUTES.SHOP.ROOT}
          onClick={() => navItemOnClickGTMEvent('Shop')}
          className={cx(s.navLink, s.navLinkSuppliers, s.navLinkShop)}
        >
          Shop
          <NewBadge className={s.navNewBadge} />
        </Link>
      )}
    </li>
  ) : null

  const sliderItemsArray = useMemo(
    () =>
      menuItems.map((item) =>
        getSliderItems(
          item.mainItem.metadata || {
            slug: item.mainItem.article_categories?.slug,
          },
          featuredArticles,
          navbarSuppliers,
          navbarVenues
        )
      ),
    [menuItems, featuredArticles, navbarSuppliers, navbarVenues]
  )

  return (
    <nav className={s.mainNav} ref={containerRef}>
      <ul className={s.navList}>
        {menuItems.map((item, index) => {
          const mainCatObj = getMainCategoryObject(item.mainItem)
          const isSuppliers = ['Suppliers', 'Venues'].includes(
            item.mainItem.metadata?.name || ''
          )

          return (
            <Fragment key={mainCatObj?.slug}>
              <li
                className={s.navItem}
                onMouseEnter={() => {
                  setHoveredCategory(mainCatObj?.slug || '')
                  handleCloseShop()
                }}
                onClick={handleCloseMenu}
              >
                <Link
                  href={`/${mainCatObj?.slug}`}
                  onClick={() => navItemOnClickGTMEvent(mainCatObj?.name)}
                  className={cx(s.navLink, {
                    [s.navLinkSuppliers]: isSuppliers,
                  })}
                >
                  {mainCatObj?.name}
                </Link>
              </li>
              {index === venuesIndex && shopNavItem}
            </Fragment>
          )
        })}
        {venuesIndex === -1 && shopNavItem}
      </ul>
      {menuItems.map((item, idx) => {
        const mainCatObj = getMainCategoryObject(item.mainItem)
        const subcategories = splitSubcategories(item.subcategories)
        const sliderItems = sliderItemsArray[idx]

        const showSlider = isDesktopSmall ? subcategories.length < 3 : true
        const dropdownOpen =
          itemHovered &&
          hoveredCategory === mainCatObj?.slug &&
          !!subcategories.length

        const cardType = item.mainItem.metadata?.showSuppliers
          ? 'Supplier'
          : item.mainItem.metadata?.showVenues
            ? 'Venue'
            : 'Article'

        return (
          <div key={mainCatObj?.slug}>
            <div
              className={cx({ [s.navOverlay]: dropdownOpen })}
              onMouseEnter={handleCloseMenu}
            />
            <div
              className={cx(s.dropdown, {
                [s.dropdownOpen]: dropdownOpen,
                [s.dropdownWithSlider]: true,
              })}
            >
              {dropdownOpen && (
                <NavDropdown
                  mainCatObj={mainCatObj}
                  subcategories={subcategories}
                  showSlider={showSlider}
                  sliderItems={sliderItems}
                  cardType={cardType}
                  handleCloseMenu={handleCloseMenu}
                />
              )}
            </div>
          </div>
        )
      })}

      {/* The Shop panel rides the same wrapper as the category dropdowns, so
          position, width and the open animation are shared. The wrapper is
          always rendered — `aria-controls` on the trigger has to point at an
          element that exists. */}
      {hasShopMenu && (
        <div>
          <div
            className={cx({ [s.navOverlay]: isShopOpen })}
            onMouseEnter={handleCloseShop}
          />
          <div
            id={SHOP_PANEL_ID}
            ref={shopPanelRef}
            className={cx(s.dropdown, s.dropdownWithSlider, {
              [s.dropdownOpen]: isShopOpen,
            })}
          >
            {isShopOpen && shopMegaMenu && (
              <NavShopMegaMenu
                data={shopMegaMenu}
                onNavigate={handleCloseShop}
              />
            )}
          </div>
        </div>
      )}
    </nav>
  )
}
